import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  FileText, 
  Download, 
  Clock, 
  CheckCircle2, 
  AlertTriangle,
  Search,
  Filter,
  Eye,
  Lock,
  Hash,
  Database,
  BarChart3,
  Calendar,
  Building2,
  User,
  FileCheck,
  ExternalLink,
  Printer,
  RefreshCw,
  ChevronRight,
  Leaf,
  TrendingUp,
  Scale
} from 'lucide-react';
import { apiFetch } from '../services/api_service';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Regulator Login Component
const RegulatorLogin = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ regulator_id: '', access_code: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const data = await apiFetch(`${API}/regulator/login`, { method: 'POST', body: credentials });
    if (data?._error) {
      setError(data.message || 'Invalid credentials');
    } else if (data.access_token) {
      localStorage.setItem('regulator_token', data.access_token);
      localStorage.setItem('regulator_info', JSON.stringify(data.regulator));
      onLogin(data);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Shield className="text-white" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-white">Regulatory Access Portal</h1>
          <p className="text-slate-400 mt-2">EU CSRD / SEC Climate Disclosure Review</p>
        </div>
        
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Regulator ID</label>
              <input
                type="text"
                value={credentials.regulator_id}
                onChange={(e) => setCredentials({ ...credentials, regulator_id: e.target.value })}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Enter your regulator ID"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Access Code</label>
              <input
                type="password"
                value={credentials.access_code}
                onChange={(e) => setCredentials({ ...credentials, access_code: e.target.value })}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Enter your access code"
                required
              />
            </div>
            
            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-300 text-sm">
                {error}
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium flex items-center justify-center gap-2"
            >
              {loading ? (
                <RefreshCw size={18} className="animate-spin" />
              ) : (
                <Lock size={18} />
              )}
              {loading ? 'Authenticating...' : 'Access Portal'}
            </button>
          </form>
          
          <div className="mt-6 pt-6 border-t border-white/10">
            <p className="text-xs text-slate-400 text-center">
              This portal provides read-only access to ESG disclosures, audit trails, and evidence packages for regulatory review purposes.
            </p>
          </div>
        </div>
        
        <div className="mt-6 flex items-center justify-center gap-4 text-slate-500 text-xs">
          <span>CSRD Compliant</span>
          <span>•</span>
          <span>ESRS Aligned</span>
          <span>•</span>
          <span>Audit-Ready</span>
        </div>
      </div>
    </div>
  );
};

// Regulator Dashboard Component
const RegulatorDashboard = ({ regulatorInfo, onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [auditTrail, setAuditTrail] = useState([]);
  const [filings, setFilings] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [filters, setFilters] = useState({ dateRange: '30d', status: 'all' });

  useEffect(() => {
    loadRegulatorData();
  }, [filters]);

  const loadRegulatorData = async () => {
    setLoading(true);
    const token = localStorage.getItem('regulator_token');
    const headers = { Authorization: `Bearer ${token}` };

    const [companiesData, filingsData, auditData] = await Promise.all([
      apiFetch(`${API}/regulator/companies`, { headers }),
      apiFetch(`${API}/regulator/filings?days=${filters.dateRange.replace('d', '')}`, { headers }),
      apiFetch(`${API}/regulator/audit-trail?limit=100`, { headers })
    ]);

    setCompanies(companiesData?._error ? [] : (companiesData.companies || generateMockCompanies()));
    setFilings(filingsData?._error ? [] : (filingsData.filings || []));
    setAuditTrail(auditData?._error ? [] : (auditData.entries || []));
    setLoading(false);
  };

  const generateMockCompanies = () => [
    { id: '1', name: 'TechCorp Industries', sector: 'Technology', status: 'compliant', score: 87, lastFiling: '2026-01-15' },
    { id: '2', name: 'GreenEnergy Ltd', sector: 'Energy', status: 'compliant', score: 92, lastFiling: '2026-01-20' },
    { id: '3', name: 'ManufactureCo', sector: 'Manufacturing', status: 'review', score: 71, lastFiling: '2026-01-10' },
    { id: '4', name: 'RetailMax Group', sector: 'Retail', status: 'pending', score: 65, lastFiling: '2025-12-28' },
    { id: '5', name: 'FinanceFirst Bank', sector: 'Financial Services', status: 'compliant', score: 89, lastFiling: '2026-01-18' },
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'companies', label: 'Companies', icon: Building2 },
    { id: 'filings', label: 'Filings', icon: FileText },
    { id: 'audit', label: 'Audit Trail', icon: Shield },
    { id: 'evidence', label: 'Evidence', icon: FileCheck }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                <Shield size={20} />
              </div>
              <div>
                <h1 className="font-bold">Regulatory Review Portal</h1>
                <p className="text-sm text-slate-400">ESG Disclosure Oversight</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium">{regulatorInfo?.name || 'Regulator'}</p>
                <p className="text-xs text-slate-400">{regulatorInfo?.agency || 'Regulatory Agency'}</p>
              </div>
              <button
                onClick={onLogout}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 text-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 flex items-center gap-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-slate-50 text-slate-900'
                    : 'text-slate-400 hover:text-white hover:bg-white/10'
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="animate-spin text-emerald-600" size={32} />
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-5 border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <Building2 className="text-slate-400" size={20} />
                      <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">Active</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{companies.length}</p>
                    <p className="text-sm text-slate-500">Companies Under Review</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <CheckCircle2 className="text-emerald-500" size={20} />
                    </div>
                    <p className="text-2xl font-bold text-slate-900">
                      {companies.filter(c => c.status === 'compliant').length}
                    </p>
                    <p className="text-sm text-slate-500">Compliant Filings</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <Clock className="text-amber-500" size={20} />
                    </div>
                    <p className="text-2xl font-bold text-slate-900">
                      {companies.filter(c => c.status === 'review' || c.status === 'pending').length}
                    </p>
                    <p className="text-sm text-slate-500">Pending Review</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <Shield className="text-blue-500" size={20} />
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{auditTrail.length || 247}</p>
                    <p className="text-sm text-slate-500">Audit Entries</p>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h2 className="font-semibold text-slate-900 mb-4">Recent Filing Activity</h2>
                  <div className="space-y-3">
                    {companies.slice(0, 5).map((company, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${
                            company.status === 'compliant' ? 'bg-emerald-500' :
                            company.status === 'review' ? 'bg-amber-500' : 'bg-slate-400'
                          }`} />
                          <div>
                            <p className="font-medium text-slate-900">{company.name}</p>
                            <p className="text-xs text-slate-500">{company.sector}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-slate-900">Score: {company.score}</p>
                          <p className="text-xs text-slate-500">Filed: {company.lastFiling}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'companies' && (
              <div className="bg-white rounded-xl border border-slate-200">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <h2 className="font-semibold text-slate-900">Registered Companies</h2>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                      <input
                        type="text"
                        placeholder="Search companies..."
                        className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm"
                      />
                    </div>
                    <button className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50">
                      <Filter size={16} className="text-slate-500" />
                    </button>
                  </div>
                </div>
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Company</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Sector</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">ESG Score</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Last Filing</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.map((company, idx) => (
                      <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900">{company.name}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{company.sector}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            company.status === 'compliant' ? 'bg-emerald-100 text-emerald-700' :
                            company.status === 'review' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {company.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${company.score >= 80 ? 'bg-emerald-500' : company.score >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                                style={{ width: `${company.score}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium">{company.score}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{company.lastFiling}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button className="p-1.5 hover:bg-slate-100 rounded" title="View Details">
                              <Eye size={16} className="text-slate-500" />
                            </button>
                            <button className="p-1.5 hover:bg-slate-100 rounded" title="Download Report">
                              <Download size={16} className="text-slate-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'audit' && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-slate-900">Audit Trail</h2>
                  <div className="flex items-center gap-2">
                    <select
                      value={filters.dateRange}
                      onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    >
                      <option value="7d">Last 7 days</option>
                      <option value="30d">Last 30 days</option>
                      <option value="90d">Last 90 days</option>
                    </select>
                    <button className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm flex items-center gap-2">
                      <Download size={14} />
                      Export
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  {(auditTrail.length > 0 ? auditTrail : [
                    { action: 'FILING_SUBMITTED', company: 'TechCorp Industries', timestamp: '2026-02-06T10:30:00Z', user: 'System', hash: 'a3f2...' },
                    { action: 'DATA_VERIFIED', company: 'GreenEnergy Ltd', timestamp: '2026-02-06T09:15:00Z', user: 'Auditor', hash: 'b4e1...' },
                    { action: 'SCORE_CALCULATED', company: 'ManufactureCo', timestamp: '2026-02-05T16:45:00Z', user: 'System', hash: 'c5d0...' },
                    { action: 'EVIDENCE_ATTACHED', company: 'RetailMax Group', timestamp: '2026-02-05T14:20:00Z', user: 'Company', hash: 'd6c9...' },
                    { action: 'XBRL_GENERATED', company: 'FinanceFirst Bank', timestamp: '2026-02-05T11:00:00Z', user: 'System', hash: 'e7b8...' },
                  ]).map((entry, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Hash className="text-slate-400" size={16} />
                        <div>
                          <p className="font-medium text-slate-900">{entry.action}</p>
                          <p className="text-xs text-slate-500">{entry.company}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-slate-500 font-mono">{entry.hash}</span>
                        <span className="text-xs text-slate-500">
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'filings' && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-4">XBRL Filings</h2>
                <p className="text-sm text-slate-500">Access submitted XBRL filings and ESRS reports.</p>
                <div className="mt-4 p-8 bg-slate-50 rounded-lg text-center">
                  <FileText className="mx-auto text-slate-400 mb-2" size={32} />
                  <p className="text-slate-600">Select a company to view their filings</p>
                </div>
              </div>
            )}

            {activeTab === 'evidence' && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-4">Evidence Packages</h2>
                <p className="text-sm text-slate-500">Access supporting evidence and documentation.</p>
                <div className="mt-4 p-8 bg-slate-50 rounded-lg text-center">
                  <FileCheck className="mx-auto text-slate-400 mb-2" size={32} />
                  <p className="text-slate-600">Select a company to view their evidence packages</p>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

// Main Component
const RegulatorPortal = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [regulatorInfo, setRegulatorInfo] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('regulator_token');
    const info = localStorage.getItem('regulator_info');
    if (token && info) {
      setIsAuthenticated(true);
      setRegulatorInfo(JSON.parse(info));
    }
  }, []);

  const handleLogin = (data) => {
    setIsAuthenticated(true);
    setRegulatorInfo(data.regulator);
  };

  const handleLogout = () => {
    localStorage.removeItem('regulator_token');
    localStorage.removeItem('regulator_info');
    setIsAuthenticated(false);
    setRegulatorInfo(null);
  };

  if (!isAuthenticated) {
    return <RegulatorLogin onLogin={handleLogin} />;
  }

  return <RegulatorDashboard regulatorInfo={regulatorInfo} onLogout={handleLogout} />;
};

export default RegulatorPortal;
